/**
 * RiderReadOnlyView — la page rider d'un membre SANS aucun droit sur le contenu rider
 * (cas `viewer`), rendue en LECTURE PURE.
 *
 * POURQUOI (L2, 2026-10-12) : la page rider rendait toute la config avec des contrôles
 * `disabled` (toggle de transport, cases à cocher des musiciens/crew, sélecteur de stage
 * plot, bouton « Import AI ») plus une note « read-only account » — pour un `viewer`,
 * l'écran RESSEMBLAIT à un formulaire d'édition interdit. La décision produit est de
 * CACHER ce qui ne sert pas au lieu de le griser : on lit, on n'édite pas.
 *
 * PATRON repris de `DaysheetViewer` (v0.725) : renderer par section, AUCUN champ de
 * saisie, et le bouton d'action (ici « Download PDF », rendu par la PAGE dans son
 * `PageHeader`) reste disponible. Rien n'est persisté ici : le composant est purement
 * présentationnel — l'écran ÉDITABLE reste celui de `RiderPage` pour owner/editor/tech
 * et pour tout titulaire de `content.rider` ou d'une section (invariant §8 de
 * `client-write-gate-2026.md`).
 */
import { useTranslation } from 'react-i18next';
import { sortMusicians, sortCrew } from '../lib/peopleSort';
import StagePlotThumb from './StagePlotEditor/StagePlotThumb';
import type { CrewMember, Musician, TransportType } from '../types';
import type { StagePlotListItem } from '../api/stageplots';

/**
 * Libellés de transport de l'écran (même table que `TransportToggle`) : le viewer LIT le
 * transport de la sélection par défaut du groupe, il ne le choisit pas.
 */
const TRANSPORT_LABEL: Record<string, string> = {
  van: '🚐 Van',
  tourbus: '🚌 Tourbus',
  train: '🚆 Train',
  plane: '✈️ Plane',
};

interface Props {
  /** Sélection par défaut du groupe (`rider_config.data.rider.transport`). */
  transport: TransportType | '' | null | undefined;
  musicians: Musician[];
  crew: CrewMember[];
  activeMusicianIds: string[];
  activeCrewIds: string[];
  /** Les stage plots du groupe — affichés en IMAGE, sans sélecteur ni création. */
  stagePlots: StagePlotListItem[];
}

export default function RiderReadOnlyView({
  transport, musicians, crew, activeMusicianIds, activeCrewIds, stagePlots,
}: Props) {
  const { t } = useTranslation();
  const active = <T extends { id: string }>(list: T[], ids: string[]) =>
    list.filter((x) => ids.includes(x.id));

  // MÊME ordre que la page rider éditable et que le PDF : main → session →
  // guest, puis `order`, puis `name` (source unique : `lib/peopleSort`). La
  // liste du line-up n'était pas triée du tout avant le 2026-10-17.
  const lineupMusicians = sortMusicians(active(musicians, activeMusicianIds));
  const lineupCrew = sortCrew(active(crew, activeCrewIds));

  return (
    <div className="rider-card" data-rider-readonly>
      <div className="rider-card-section">
        <h2>{t('riderPage.transport')}</h2>
        {/* TEXTE, pas de toggle : le viewer ne choisit pas le transport du groupe. */}
        <p className="rider-readonly-value">
          {transport ? (TRANSPORT_LABEL[transport] || transport) : '—'}
        </p>
      </div>

      <div className="rider-card-section">
        <h2>{t('riderPage.activeMusicians')}</h2>
        {lineupMusicians.length === 0 ? (
          <p className="empty-hint">{t('riderPage.noMusicians')}</p>
        ) : (
          <ul className="rider-readonly-list">
            {lineupMusicians.map((m) => (
              <li key={m.id}>
                <span className="musician-dot" style={{ backgroundColor: m.color }} />
                {m.name || t('musicians.unnamed')}
                {m.fonction && <span className="musician-role-tag">{m.fonction}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rider-card-section">
        <h2>{t('riderPage.activeCrew')}</h2>
        {lineupCrew.length === 0 ? (
          <p className="empty-hint">{t('riderPage.noCrew')}</p>
        ) : (
          <ul className="rider-readonly-list">
            {lineupCrew.map((c) => (
              <li key={c.id}>
                <span className="crew-dot">👤</span>
                {c.name || c.role || t('crew.unnamed')}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rider-card-section">
        <h2>{t('riderPage.stagePlot')}</h2>
        {stagePlots.length === 0 ? (
          <p className="empty-hint">{t('riderPage.noStagePlots')}</p>
        ) : (
          <div className="stageplot-thumbnails">
            {stagePlots.map((plot) => (
              // IMAGE seule : pas de sélecteur, pas de « Create new stage plot ».
              <div key={plot.id} className="stageplot-thumb-card rider-readonly-thumb">
                <StagePlotThumb plot={plot.data} selected={false} />
                <span className="stageplot-thumb-name">{plot.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
